import { ReactNode, useCallback, useState } from 'react';
import { GrantInfoHelpPanel } from '../components/events/createEvent/components/GrantInfoHelpPanel';
import { ShiftTypeInfoHelpPanel } from '../components/events/createEvent/components/ShiftTypeInfoHelpPanel';
import { CommunityOrganizationHelpPanel } from '../components/events/createEvent/components/CommunityOrganizationHelpInfo';
import { WaitlistInfoHelpPanel } from '../components/myActivity/waitlistInfoHelpPanel';
import { CoordinatorNotificationHelpPanel } from '../components/events/createEvent/components/CoordinatorNotificationHelpPanel';

type PanelId =
  | 'grant'
  | 'shiftType'
  | 'communityOrganization'
  | 'waitlist'
  | 'coordinatorNotification';

const PANELS: Record<PanelId, () => ReactNode> = {
  grant: () => <GrantInfoHelpPanel />,
  shiftType: () => <ShiftTypeInfoHelpPanel />,
  communityOrganization: () => <CommunityOrganizationHelpPanel />,
  waitlist: () => <WaitlistInfoHelpPanel />,
  coordinatorNotification: () => <CoordinatorNotificationHelpPanel />,
};

interface ToolsController {
  setTools: (content: ReactNode) => void;
  setToolsOpen: (open: boolean) => void;
}

/**
 * Hook for managing wizard help panels.
 *
 * Pass a controller (from outletContext) when in new nav mode.
 * The hook will open/close the AppLayout tools panel directly via the controller,
 * making AppLayout the single source of truth for open/close state.
 *
 * When no controller is passed (legacy mode), the hook manages its own local state
 * and returns props to spread onto the page's own AppLayout.
 */
export function useWizardToolsPanel(controller?: ToolsController | null) {
  // Legacy mode state (only used when controller is not provided)
  const [legacyOpen, setLegacyOpen] = useState(false);
  const [panelId, setPanelId] = useState<PanelId>('grant');

  const open = useCallback(
    (id: PanelId) => {
      setPanelId(id);
      if (controller) {
        // New nav: push content and open state up to AppLayout
        controller.setTools(PANELS[id]());
        controller.setToolsOpen(true);
      } else {
        // Legacy: manage locally
        setLegacyOpen(true);
      }
    },
    [controller]
  );
  const openWaitlistPanel = useCallback(() => open('waitlist'), [open]);

  const close = useCallback(() => {
    if (controller) {
      controller.setToolsOpen(false);
    } else {
      setLegacyOpen(false);
    }
  }, [controller]);

  const openGrantPanel = useCallback(() => open('grant'), [open]);
  const openShiftTypePanel = useCallback(() => open('shiftType'), [open]);
  const openCommunityOrganizationPanel = useCallback(() => open('communityOrganization'), [open]);
  const openCoordinatorNotificationPanel = useCallback(
    () => open('coordinatorNotification'),
    [open]
  );

  // Legacy AppLayout props (only meaningful when no controller)
  const onToolsChange = useCallback(
    ({ detail }: { detail: { open: boolean } }) => setLegacyOpen(detail.open),
    []
  );

  return {
    // Legacy AppLayout props
    toolsOpen: legacyOpen,
    toolsHide: !legacyOpen,
    tools: PANELS[panelId](),
    onToolsChange,
    // Shared actions
    openGrantPanel,
    openShiftTypePanel,
    openCommunityOrganizationPanel,
    openWaitlistPanel,
    openCoordinatorNotificationPanel,
    close,
  };
}
